/**
 * NBPLCCarbonStructuring — Carbon Asset Assembly Layer
 * Route: /corporate/nbplc/carbon-structuring
 *
 * Page positioning:
 *   "The layer where identified opportunities are structured into carbon programmes
 *    and routed toward market pathways"
 *
 * Logical flow (mandatory order):
 *   1. Header
 *   2. Structuring Overview
 *   3. Carbon Pathways (VCM / Article 6)
 *   4. CPA Grouping
 *   5. Programme of Activities (PoA)
 *   6. Portfolio Aggregation
 *
 * Data: reads from vbCache (same SSOT as Carbon Strategy + Financing)
 * PoA lives HERE — not a separate sidebar item
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useProjectStore,
  CPA_GROUPS, CPA_BY_TYPE,
} from "@/store/projectStore";
import { PageHeader, SectionCard } from "@/components/ds";
import {
  RefreshCw, ArrowRight, CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronUp, Download, Info, Shield
} from "lucide-react";
import client from "@/api/client";

// ── LoA Pack download (reuses backend endpoint) ───────────────────────────────
function LoAButton({ oppId }) {
  const [generating, setGen] = useState(false);
  const handleLoA = async () => {
    setGen(true);
    try {
      const resp = await client.get(`/nbplc/pdf/loa/${oppId}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `NB_Art6_LoA_${oppId}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { alert("LoA generation not available for this opportunity type."); }
    finally { setGen(false); }
  };
  return (
    <button onClick={handleLoA} disabled={generating}
      className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-bold rounded-lg disabled:opacity-50 transition-colors">
      {generating ? <RefreshCw size={9} className="animate-spin"/> : <Download size={9}/>}
      {generating ? "Generating..." : "Draft LoA Pack"}
    </button>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function NBPLCCarbonStructuring() {
  const navigate = useNavigate();
  const {
    selectedPeriod, period, invalidateVb, evidencePct,
    projects: allOpps, inStructuring, poaProjects, vcmProjects, art6Projects,
    cpaGroups: cpas,
    portfolio,
    vbData, vbLoading,
  } = useProjectStore();
  const [expandedCpa, setExpandedCpa] = useState(null);
  const [transitioning, setTrans] = useState(false);
  const loading = vbLoading && !vbData;

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/nbplc/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setTrans(false);
  };

  // Derived from store data (no local re-computation)
  const vcmEligible  = vcmProjects;
  const art6Eligible = art6Projects;
  const poaEligible  = poaProjects;
  const totalEligibleRed = poaProjects.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);
  const totalRed         = allOpps.reduce((s,o) => s + (o.reduction_tco2e_yr||0), 0);

  // Portfolio readiness score
  const avgScore = useMemo(() => {
    if (!poaEligible.length) return 0;
    return Math.round(poaEligible.reduce((s,o) => s+(o.composite_score||0),0) / poaEligible.length);
  }, [poaEligible]);
  const poaCoverage = totalRed > 0 ? Math.round(totalEligibleRed / totalRed * 100) : 0;
  const readinessScore = Math.min(100, Math.round(
    (evidencePct || 60) * 0.35 + avgScore * 0.40 + poaCoverage * 0.25
  ));
  const readinessLabel = readinessScore >= 70 ? "High" : readinessScore >= 50 ? "Medium" : "Low";
  const readinessCls   = readinessScore >= 70
    ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : readinessScore >= 50
    ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-red-700 bg-red-50 border-red-200";

  // Primary pathway (most eligible projects)
  const primaryPathway = art6Eligible.length >= vcmEligible.length / 2 ? "Hybrid (VCM + Art.6)" : "VCM";

  if (loading) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-8">

      {/* ── SECTION 1: HEADER ─────────────────────────────────────────────── */}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
        <PageHeader title="Carbon Structuring" />
        <p className="text-xs text-slate-400 mt-1 max-w-2xl">
          Structure eligible climate actions into carbon programmes, pathway routes, and portfolio-scale market opportunities
        </p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {[
            [`${poaEligible.length} eligible projects`, "bg-teal-50 text-teal-700 border-teal-200"],
            [`${(totalEligibleRed/1000).toFixed(0)}K tCO₂e/yr potential`, "bg-emerald-50 text-emerald-700 border-emerald-200"],
            [primaryPathway, "bg-blue-50 text-blue-700 border-blue-200"],
            [inStructuring.length > 0 ? `${inStructuring.length} in structuring` : "Ready to structure", "bg-amber-50 text-amber-700 border-amber-200"],
          ].map(([label, cls]) => (
            <span key={label} className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${cls}`}>{label}</span>
          ))}
        </div>
      </div>

      {/* ── SECTION 2: STRUCTURING OVERVIEW ──────────────────────────────── */}
      <div>
        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Structuring Overview</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Carbon Pathways",    value: "Hybrid",                    sub: "VCM + Article 6",                   color: "border-blue-400" },
            { label: "CPA Groups",         value: "3",                         sub: "Energy · Fuel · Circularity",        color: "border-amber-400" },
            { label: "PoA Status",         value: inStructuring.length > 0 ? "Structuring" : "Pre-structuring", sub: `${inStructuring.length} CPA(s) active`, color: "border-purple-400" },
            { label: "Portfolio Potential", value: `${(totalEligibleRed/1000).toFixed(0)}K tCO₂e/yr`, sub: `${poaEligible.length} of ${allOpps.length} projects eligible`, color: "border-emerald-400" },
          ].map(c => (
            <div key={c.label} className={`bg-white border border-slate-200 border-t-2 ${c.color} rounded-xl p-4`}>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-2">{c.label}</p>
              <p className="text-lg font-bold text-slate-900 leading-tight">{c.value}</p>
              <p className="text-[10px] text-slate-500 mt-0.5">{c.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── SECTION 3: CARBON PATHWAYS ───────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-blue-500 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">Carbon Pathways</p>
          <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step 1 of 4</span>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          Before grouping projects, establish the available carbon market routes.
          Each pathway has distinct requirements, timelines, and integrity standards.
          NB's portfolio is eligible for both pathways — route selection depends on project type and commercial ambition.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* VCM Card */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-teal-500 inline-block"/>
                <p className="text-xs font-bold text-slate-800">A. Voluntary Carbon Market (VCM)</p>
              </div>
              <span className="text-[9px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-0.5">
                {vcmEligible.length} projects eligible
              </span>
            </div>
            <div className="p-5">
              <div className="space-y-2 text-xs">
                {[
                  ["Standards", "Verra (VCS) · Gold Standard · ACM / AMS methodology"],
                  ["Typical use case", "Energy efficiency, renewables, packaging circularity"],
                  ["Speed to market", "18–24 months (PDD → validation → issuance)"],
                  ["Price range", "$5–$20/tCO₂e (VCM market, 2024–2026)"],
                  ["Integrity level", "High — independent VVB validation required"],
                  ["Key requirement", "Methodology registration + additionality proof"],
                ].map(([k,v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-slate-400 w-28 flex-shrink-0">{k}</span>
                    <span className="text-slate-700 font-medium">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Best suited for</p>
                <div className="flex flex-wrap gap-1.5">
                  {["Solar PV", "Biomass Boiler", "Biogas Recovery", "Returnable Bottles"].map(t => (
                    <span key={t} className="text-[10px] font-semibold px-2 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded">{t}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Article 6 Card */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"/>
                <p className="text-xs font-bold text-slate-800">B. Article 6 — ITMO Pathway</p>
              </div>
              <span className="text-[9px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                {art6Eligible.length} projects eligible
              </span>
            </div>
            <div className="p-5">
              <div className="space-y-2 text-xs">
                {[
                  ["Authorising body", "NCCC — Nigeria Climate Change Council"],
                  ["Mechanism", "Art.6.2 bilateral / Art.6.4 supervisory body"],
                  ["ITMO potential", "Country-to-country transfer of mitigation outcomes"],
                  ["Price range", "$10–$40/tCO₂e (bilateral agreement dependent)"],
                  ["Integrity level", "Highest integrity potential — subject to host country authorization (LoA) and corresponding adjustment"],
                  ["Timeline", "24–36 months (NCCC engagement + LoA + validation)"],
                ].map(([k,v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-slate-400 w-28 flex-shrink-0">{k}</span>
                    <span className="text-slate-700 font-medium">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Best suited for</p>
                <div className="flex flex-wrap gap-1.5">
                  {["Solar PV", "Biomass Boiler", "Biogas Recovery"].map(t => (
                    <span key={t} className="text-[10px] font-semibold px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded">{t}</span>
                  ))}
                  <span className="text-[10px] font-semibold px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200 rounded">Heat Recovery (not eligible)</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Routing matrix */}
        <div className="mt-4 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Pathway Routing Matrix</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-[9px] text-slate-400 pb-2 px-4 pt-3 font-semibold uppercase tracking-wide">Intervention Type</th>
                  <th className="text-center text-[9px] text-slate-400 pb-2 px-3 pt-3 font-semibold uppercase">VCM</th>
                  <th className="text-center text-[9px] text-slate-400 pb-2 px-3 pt-3 font-semibold uppercase">Article 6</th>
                  <th className="text-left text-[9px] text-slate-400 pb-2 px-3 pt-3 font-semibold uppercase tracking-wide">Methodology Signal</th>
                  <th className="text-right text-[9px] text-slate-400 pb-2 px-4 pt-3 font-semibold uppercase">Projects</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Solar PV",             true,  true,  "ACM0002 / AMS.I.A"],
                  ["Heat Recovery",        false, false, "Internal (not carbon-market eligible)"],
                  ["Biomass Boiler",       true,  true,  "AMS.I.C — Thermal energy for users"],
                  ["Biogas Recovery",      true,  true,  "AMS.III.E — Methane recovery / waste"],
                  ["Returnable Bottles",   true,  false, "AMS.III.AJ — Demand-side Scope 3"],
                  ["Logistics Optimisation",false,false, "Internal (not carbon-market eligible)"],
                ].map(([type, vcm, a6, meth]) => {
                  const count = allOpps.filter(o => o.opp_type === type.toLowerCase().replace(/ /g,"_")).length;
                  return (
                    <tr key={type} className="border-b border-slate-50 last:border-0">
                      <td className="py-2.5 px-4 font-semibold text-slate-800">{type}</td>
                      <td className="py-2.5 px-3 text-center">
                        {vcm ? <CheckCircle2 size={13} className="text-teal-500 mx-auto"/> : <XCircle size={13} className="text-slate-300 mx-auto"/>}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        {a6 ? <CheckCircle2 size={13} className="text-blue-500 mx-auto"/> : <XCircle size={13} className="text-slate-300 mx-auto"/>}
                      </td>
                      <td className="py-2.5 px-3 text-slate-500 text-[10px]">{meth}</td>
                      <td className="py-2.5 px-4 text-right text-slate-500 font-semibold">{count}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── SECTION 4: CPA GROUPING ──────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-amber-500 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">CPA Grouping</p>
          <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step 2 of 4</span>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          Eligible projects are grouped into Component Project Activities (CPAs) by intervention type.
          Grouping by technology category ensures methodological consistency and simplifies
          PoA assembly, monitoring, and verification.
        </p>

        <div className="space-y-3">
          {cpas.map((cpa, idx) => (
            <div key={cpa.id}
              style={{ background: cpa.bg, borderColor: cpa.border }}
              className="border rounded-xl overflow-hidden">
              <button
                onClick={() => setExpandedCpa(expandedCpa === cpa.id ? null : cpa.id)}
                className="w-full px-5 py-4 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ background: cpa.color }}>
                    {cpa.id}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{cpa.label}</p>
                    <p className="text-[10px] text-slate-500">{cpa.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-xs font-bold text-slate-700">{(cpa.totalRed/1000).toFixed(0)}K tCO₂e/yr</p>
                    <p className="text-[9px] text-slate-400">{cpa.projects.length} projects · {cpa.poaProjects.length} PoA-eligible</p>
                  </div>
                  <div className="flex gap-1.5">
                    {cpa.pathways.map(p => (
                      <span key={p} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                        p === "VCM" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-blue-50 text-blue-700 border-blue-200"
                      }`}>{p}</span>
                    ))}
                  </div>
                  {expandedCpa === cpa.id ? <ChevronUp size={14} className="text-slate-400"/> : <ChevronDown size={14} className="text-slate-400"/>}
                </div>
              </button>

              {expandedCpa === cpa.id && (
                <div className="px-5 pb-4 pt-0 border-t" style={{borderColor: cpa.border}}>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-xs mb-3">
                    <div><p className="text-slate-400 mb-1">Methodology signal</p><p className="font-semibold text-slate-700">{cpa.methodology}</p></div>
                    <div><p className="text-slate-400 mb-1">Pathways</p><p className="font-semibold text-slate-700">{cpa.pathways.join(" + ")}</p></div>
                    <div><p className="text-slate-400 mb-1">Total reduction</p><p className="font-semibold text-slate-700">{(cpa.totalRed/1000).toFixed(1)}K tCO₂e/yr</p></div>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left text-[9px] text-slate-400 pb-1.5 font-semibold uppercase">Project</th>
                        <th className="text-left text-[9px] text-slate-400 pb-1.5 font-semibold uppercase">Plant</th>
                        <th className="text-right text-[9px] text-slate-400 pb-1.5 font-semibold uppercase">Reduction</th>
                        <th className="text-center text-[9px] text-slate-400 pb-1.5 font-semibold uppercase">PoA Eligible</th>
                        <th className="text-left text-[9px] text-slate-400 pb-1.5 font-semibold uppercase">Additionality</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cpa.projects.map(o => {
                        const add = o.additionality;
                        const eligible = o.vcm_eligible || o.art6_eligible;
                        return (
                          <tr key={o.opp_id} className="border-b border-slate-100/50 last:border-0">
                            <td className="py-2 pr-3 font-semibold text-slate-800 max-w-[180px] truncate">{o.name}</td>
                            <td className="py-2 pr-3 text-slate-500">{(o.bu_name||"").replace("NB ","").split(" ")[0]}</td>
                            <td className="py-2 pr-3 text-right font-bold text-slate-700">{Number(o.reduction_tco2e_yr||0).toLocaleString()} t</td>
                            <td className="py-2 pr-3 text-center">
                              {eligible
                                ? <CheckCircle2 size={12} className="text-emerald-500 mx-auto"/>
                                : <XCircle size={12} className="text-slate-300 mx-auto"/>}
                            </td>
                            <td className="py-2">
                              <p className={`text-[10px] font-semibold leading-tight ${add.cls}`}>{add.primary}</p>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── SECTION 5: PROGRAMME OF ACTIVITIES (PoA) ─────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-purple-500 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">Programme of Activities (PoA)</p>
          <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step 3 of 4</span>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          Grouped CPAs roll into one Programme of Activities. The PoA umbrella structure enables
          portfolio-scale registration, simplified monitoring, and improved financing relevance.
          Not all projects qualify — PoA eligibility requires carbon market pathway fit and additionality evidence.
        </p>

        {/* PoA Hierarchy visual */}
        <div className="bg-slate-900 rounded-xl p-5 mb-4">
          <p className="text-[9px] text-slate-400 uppercase tracking-widest font-bold mb-4">PoA Umbrella Structure</p>
          <div className="flex items-start gap-4">
            {/* PoA node */}
            <div className="bg-slate-700 border border-slate-600 rounded-xl p-3 text-center flex-shrink-0 min-w-[130px]">
              <p className="text-[9px] text-slate-400 uppercase tracking-wide">PoA</p>
              <p className="text-xs font-bold text-white mt-1">NB Breweries<br/>Decarbonisation Programme</p>
              <p className="text-[9px] text-slate-400 mt-1">{cpas.length} CPAs · {poaEligible.length} projects</p>
            </div>
            {/* Arrow */}
            <div className="flex flex-col items-center justify-center h-full pt-5">
              <div className="w-6 h-0.5 bg-slate-600"/>
            </div>
            {/* CPAs */}
            <div className="flex gap-3 flex-1 flex-wrap">
              {cpas.map(cpa => (
                <div key={cpa.id}
                  className="rounded-xl p-3 text-center flex-1 min-w-[120px]"
                  style={{ background: cpa.color + "22", border: `1px solid ${cpa.color}44` }}>
                  <p className="text-[9px] font-bold uppercase tracking-wide" style={{color: cpa.color}}>{cpa.id}</p>
                  <p className="text-xs font-bold text-white mt-1">{cpa.label}</p>
                  <p className="text-[9px] mt-1" style={{color: cpa.color + "cc"}}>{cpa.poaProjects.length} eligible projects</p>
                  <p className="text-[9px] mt-0.5" style={{color: cpa.color + "cc"}}>{(cpa.poaProjects.reduce((s,o)=>s+(o.reduction_tco2e_yr||0),0)/1000).toFixed(0)}K tCO₂e/yr</p>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-700 flex items-center gap-4 text-[10px] text-slate-400">
            <span>Total CPAs: {cpas.length}</span>
            <span>·</span>
            <span>Total eligible projects: {poaEligible.length}</span>
            <span>·</span>
            <span>Total reduction: {(totalEligibleRed/1000).toFixed(0)}K tCO₂e/yr</span>
          </div>
        </div>

        {/* PoA Eligibility table */}
        <SectionCard>
          <p className="text-xs font-bold text-slate-700 mb-1">PoA Eligibility Assessment</p>
          <p className="text-[10px] text-slate-400 mb-3">
            Not all projects are automatically PoA-eligible. Eligibility requires carbon market pathway fit,
            additionality evidence, and methodology alignment.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-slate-100">
                  {["Project","CPA","PoA Eligible","Additionality Signal (Preliminary)","Pathway","Issuance Readiness","Status","Action"].map(h => (
                    <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allOpps.slice(0, 12).map(o => {
                  const cpa = CPA_BY_TYPE[o.opp_type];
                  const eligible = o.vcm_eligible || o.art6_eligible;
                  const add = o.additionality;
                  const ir = o.issuance_readiness;
                  const pathway = o.art6_eligible ? "VCM + Art.6" : o.vcm_eligible ? "VCM" : "Internal";
                  const isStructuring = ["structuring_in_progress","structuring_ready"].includes(o.status);
                  const canSend = o.status === "promoted_to_strategy";
                  return (
                    <tr key={o.opp_id} className="border-b border-slate-50 last:border-0">
                      <td className="py-2.5 pr-3 font-semibold text-slate-800 max-w-[150px] truncate">{o.name}</td>
                      <td className="py-2.5 pr-3">
                        {cpa ? (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{background: cpa.color+"22", color: cpa.color}}>{cpa.id}</span>
                        ) : <span className="text-[9px] text-slate-400">—</span>}
                      </td>
                      <td className="py-2.5 pr-3">
                        {eligible
                          ? <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">Yes</span>
                          : <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">Internal</span>}
                      </td>
                      <td className="py-2.5 pr-3">
                        <p className={`text-[10px] font-semibold leading-tight ${add.cls}`}>{add.primary}</p>
                        <p className="text-[9px] text-slate-400">{add.sub}</p>
                      </td>
                      <td className="py-2.5 pr-3 text-[10px] text-slate-500 whitespace-nowrap">{pathway}</td>
                      <td className="py-2.5 pr-3">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${ir.cls}`}>
                          {ir.label}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                          isStructuring ? "bg-amber-100 text-amber-700 border-amber-200" :
                          o.status === "financing_review" ? "bg-purple-100 text-purple-700 border-purple-200" :
                          "bg-slate-100 text-slate-500 border-slate-200"
                        }`}>{o.status.replace(/_/g," ")}</span>
                      </td>
                      <td className="py-2.5">
                        {eligible && canSend ? (
                          <button onClick={() => handleTransition(o.opp_id, "structuring_in_progress")}
                            disabled={transitioning}
                            className="text-[9px] font-bold px-2 py-1 bg-[#0F172A] text-white rounded-lg hover:bg-slate-700 whitespace-nowrap">
                            → Structuring
                          </button>
                        ) : isStructuring ? (
                          <div className="flex items-center gap-1.5">
                            <LoAButton oppId={o.opp_id} />
                            <button onClick={() => handleTransition(o.opp_id, "financing_review")}
                              disabled={transitioning}
                              className="text-[9px] font-bold px-2 py-1 bg-slate-700 text-white rounded-lg hover:bg-slate-600 whitespace-nowrap">
                              → Financing
                            </button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-start gap-1.5">
            <Info size={10} className="text-slate-400 flex-shrink-0 mt-0.5"/>
            <p className="text-[9px] text-slate-400 italic">
              Article 6 readiness and PoA structuring are preparation activities only. Not approval or issuance.
              Host country authorization (NCCC) required before any ITMO can be issued.
            </p>
          </div>
        </SectionCard>
      </div>

      {/* ── SECTION 6: PORTFOLIO AGGREGATION ─────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-1 h-5 bg-emerald-500 rounded-full flex-shrink-0"/>
          <p className="text-sm font-bold text-slate-900">Portfolio Aggregation</p>
          <span className="text-[9px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5 ml-1">Step 4 of 4</span>
        </div>
        <p className="text-xs text-slate-500 mb-4 max-w-2xl">
          The aggregated portfolio view shows the combined output of the structuring process —
          eligible projects, total reduction potential, pathway split, and indicative carbon value scenarios.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Aggregated totals */}
          <div className="lg:col-span-2 space-y-4">
            <SectionCard>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Portfolio Composition</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["CPA","Projects","Eligible","Reduction tCO₂e/yr","Pathway"].map(h => (
                      <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cpas.map(cpa => (
                    <tr key={cpa.id} className="border-b border-slate-50 last:border-0">
                      <td className="py-2.5 pr-3">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{background: cpa.color+"22", color: cpa.color}}>{cpa.id}</span>
                        <span className="ml-1.5 text-slate-700 font-semibold">{cpa.label}</span>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-500">{cpa.projects.length}</td>
                      <td className="py-2.5 pr-3 font-bold text-emerald-700">{cpa.poaProjects.length}</td>
                      <td className="py-2.5 pr-3 font-bold text-slate-900">{(cpa.poaProjects.reduce((s,o)=>s+(o.reduction_tco2e_yr||0),0)/1000).toFixed(1)}K</td>
                      <td className="py-2.5">
                        <div className="flex gap-1 flex-wrap">
                          {cpa.pathways.map(p => (
                            <span key={p} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${
                              p === "VCM" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-blue-50 text-blue-700 border-blue-200"
                            }`}>{p}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50">
                    <td className="py-2.5 pr-3 font-bold text-slate-900">TOTAL</td>
                    <td className="py-2.5 pr-3 font-bold">{allOpps.length}</td>
                    <td className="py-2.5 pr-3 font-bold text-emerald-700">{poaEligible.length}</td>
                    <td className="py-2.5 pr-3 font-bold text-emerald-700">{(totalEligibleRed/1000).toFixed(1)}K</td>
                    <td className="py-2.5 text-[10px] text-slate-500">Hybrid (VCM + Art.6)</td>
                  </tr>
                </tbody>
              </table>
            </SectionCard>

            {/* Indicative value scenarios */}
            <SectionCard>
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Indicative Annual Carbon Value Scenarios</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase">Scenario</th>
                    <th className="text-right text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase">$/tCO₂e</th>
                    <th className="text-right text-[9px] text-slate-400 pb-2 pr-3 font-semibold uppercase">VCM Indicative/yr</th>
                    <th className="text-right text-[9px] text-slate-400 pb-2 font-semibold uppercase">Article 6 Indicative/yr</th>
                  </tr>
                </thead>
                <tbody>
                  {[["Low","$5","$10"],["Base","$10","$20"],["High","$20","$40"]].map(([label, vcmP, a6P]) => {
                    const vcmVal = Math.round(totalEligibleRed * parseInt(vcmP.replace("$","")) / 10000) * 10000;
                    const a6Val  = Math.round(art6Eligible.reduce((s,o)=>s+(o.reduction_tco2e_yr||0),0) * parseInt(a6P.replace("$","")) / 10000) * 10000;
                    const fmt = v => v >= 1_000_000 ? `$${(v/1e6).toFixed(1)}M` : `$${(v/1000).toFixed(0)}K`;
                    return (
                      <tr key={label} className="border-b border-slate-50 last:border-0">
                        <td className="py-2 pr-3 font-semibold text-slate-700">{label}</td>
                        <td className="py-2 pr-3 text-right text-slate-500">{vcmP}</td>
                        <td className="py-2 pr-3 text-right font-semibold text-slate-600 italic">{fmt(vcmVal)}</td>
                        <td className="py-2 text-right font-semibold text-blue-600 italic">{fmt(a6Val)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[9px] text-slate-400 italic mt-2 flex items-start gap-1.5">
                <AlertTriangle size={9} className="text-amber-400 flex-shrink-0 mt-0.5"/>
                Indicative only — subject to methodology validation, authorization, verification, issuance, and market conditions.
              </p>
            </SectionCard>
          </div>

          {/* Portfolio readiness — 4-component breakdown */}
          <div className="space-y-4">
            <div className={`border rounded-xl p-5 ${readinessCls}`} data-testid="poa-readiness">
              <p className="text-[9px] font-bold uppercase tracking-widest mb-3">Portfolio Readiness</p>
              {(() => {
                const dataScore     = Math.round(evidencePct||60);
                const methodScore   = Math.min(100, Math.round(poaEligible.length / Math.max(allOpps.length,1) * 100 + 20));
                const addScore      = Math.min(100, Math.round(
                  poaEligible.filter(o=>(o.irr_pct||0)<=22).length / Math.max(poaEligible.length,1) * 100
                ));
                const maturityScore = Math.min(100, Math.round(inStructuring.length / Math.max(poaEligible.length,1) * 100 + 15));
                const composite     = Math.min(100, Math.round(dataScore*0.25 + methodScore*0.30 + addScore*0.25 + maturityScore*0.20));
                return (
                  <>
                    <div className="space-y-2.5 mb-4">
                      {[
                        ["Data Completeness",     dataScore,     "Evidence uploads vs required"],
                        ["Methodology Alignment", methodScore,   "VCM/Art.6 pathway fit"],
                        ["Additionality Strength",addScore,      "Investment barrier cases"],
                        ["Structuring Maturity",  maturityScore, "Pipeline stage progression"],
                      ].map(([label, score, desc]) => (
                        <div key={label}>
                          <div className="flex justify-between text-[10px] mb-1">
                            <span className="font-semibold">{label}</span>
                            <span className="font-bold">{score}%</span>
                          </div>
                          <div className="h-1.5 bg-white/40 rounded-full overflow-hidden">
                            <div className="h-full rounded-full bg-current transition-all" style={{width:`${score}%`, opacity:0.65}}/>
                          </div>
                          <p className="text-[9px] mt-0.5 opacity-60">{desc}</p>
                        </div>
                      ))}
                    </div>
                    <div className="pt-3 border-t border-current/20 flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold">Composite Score</p>
                        <p className="text-[9px] mt-0.5">{readinessLabel} Readiness</p>
                      </div>
                      <p className="text-3xl font-black">{composite}<span className="text-sm font-normal">/100</span></p>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Next steps */}
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mb-3">Structuring Next Steps</p>
              <div className="space-y-2">
                {[
                  { done: (evidencePct||60) >= 60, label: "Evidence completeness ≥60%" },
                  { done: inStructuring.length >= 2, label: "≥2 projects in structuring" },
                  { done: false, label: "PDD preparation initiated" },
                  { done: false, label: "VVB engaged for validation" },
                  { done: false, label: "NCCC engagement (Art.6)" },
                ].map(({done, label}) => (
                  <div key={label} className="flex items-center gap-2 text-xs">
                    {done
                      ? <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0"/>
                      : <div className="w-3 h-3 rounded-full border-2 border-slate-300 flex-shrink-0"/>}
                    <span className={done ? "text-slate-700" : "text-slate-400"}>{label}</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={() => navigate("/corporate/nbplc/financing")}
              className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800">
              <ArrowRight size={12}/> Advance to Financing
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <Shield size={13} className="text-amber-500 flex-shrink-0 mt-0.5"/>
          <p className="text-xs text-amber-700">
            <strong>Carbon Structuring is a preparation activity only.</strong>{" "}
            No ITMOs have been issued, transferred, or authorised. All pathway fit assessments,
            CPA groupings, and PoA structures are indicative. Formal authorisation requires VVB validation
            and NCCC engagement. Carbon value must not be recognised in financial statements until
            all conditions are confirmed.
          </p>
        </div>
      </div>

    </div>
  );
}
